import { useEffect, useMemo, useState } from 'react';
import { CheckSquare } from 'lucide-react';
import { useTasksStore, type TaskStatus } from '@/stores/tasksStore.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { TaskCard } from '@/components/tasks/TaskCard.js';
import { useNavigate } from 'react-router-dom';
import { LoadingState } from '@/components/common/LoadingState.js';

interface TasksListProps {
    searchQuery: string;
}

export function TasksList({ searchQuery }: TasksListProps) {
    const { apiClient, sessionService } = useServices();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    const tasks = useTasksStore((state) => state.tasks);
    const addTask = useTasksStore((state) => state.addTask);
    const updateTask = useTasksStore((state) => state.updateTask);
    const removeTask = useTasksStore((state) => state.removeTask);

    // Load tasks from API on mount
    useEffect(() => {
        const loadTasks = async () => {
            try {
                setIsLoading(true);
                const apiTasks = await apiClient.getTasks();

                // Convert API tasks to store format
                apiTasks.forEach((apiTask) => {
                    // Determine direction: if we have a sessionId, it's outgoing (we initiated it)
                    // Otherwise it's incoming (someone delegated to us)
                    const direction = apiTask.sessionId ? 'outgoing' : 'incoming';

                    // Map API status to store status
                    let status: TaskStatus = apiTask.status as TaskStatus;
                    // Incoming tasks without sessionId that are 'submitted' should be 'pending_approval'
                    if (direction === 'incoming' && apiTask.status === 'submitted') {
                        status = 'pending_approval';
                    }

                    addTask({
                        taskId: apiTask.taskId,
                        direction,
                        status: status as any,
                        remoteAgent: apiTask.remoteAgent,
                        remoteAgentName: apiTask.remoteAgentName,
                        request: apiTask.request,
                        response: apiTask.response,
                        error: apiTask.error,
                        sessionId: apiTask.sessionId,
                        createdAt: apiTask.createdAt,
                        updatedAt: apiTask.updatedAt,
                        completedAt: apiTask.completedAt,
                    });
                });
            } catch (error) {
                console.error('Failed to load tasks:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadTasks();
    }, [apiClient, addTask]);

    // Get all tasks sorted by creation date (newest first)
    const allTasks = useMemo(() => {
        const taskArray = Array.from(tasks.values()).sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        if (!searchQuery) return taskArray;

        const query = searchQuery.toLowerCase();
        return taskArray.filter(
            (task) =>
                task.request.text.toLowerCase().includes(query) ||
                task.remoteAgent.toLowerCase().includes(query) ||
                task.remoteAgentName?.toLowerCase().includes(query)
        );
    }, [tasks, searchQuery]);

    const handleAccept = async (taskId: string) => {
        try {
            const task = tasks.get(taskId);
            if (!task) {
                console.error('Task not found:', taskId);
                return;
            }

            // Create a new session for this delegation
            const session = await apiClient.createSession();

            // Update task status to accepted and set sessionId
            updateTask(taskId, {
                status: 'working',
                sessionId: session.session.id,
            });

            // Navigate to the new session
            navigate(`/chat/${session.session.id}`);

            // Send the delegated message to the agent for processing
            // This will trigger the LLM to process the request and emit thinking/chunk/response events
            try {
                await apiClient.sendMessage({
                    message: task.request.text,
                    sessionId: session.session.id,
                });
            } catch (error) {
                console.error('Failed to send delegated message to agent:', error);
                // Don't throw - we've already navigated, just log the error
            }
        } catch (error) {
            console.error('Failed to accept task:', error);
        }
    };

    const handleReject = async (taskId: string) => {
        try {
            // Update task status to rejected
            updateTask(taskId, {
                status: 'rejected',
            });

            // TODO: Backend should notify the sender that we rejected
        } catch (error) {
            console.error('Failed to reject task:', error);
        }
    };

    const handleDelete = async (taskId: string) => {
        try {
            await apiClient.deleteTask(taskId);
            removeTask(taskId);
        } catch (error) {
            console.error('Failed to delete task:', error);
        }
    };

    const handleViewDetails = async (taskId: string) => {
        const task = tasks.get(taskId);
        if (task?.sessionId) {
            try {
                await sessionService.switchSession(task.sessionId);
                navigate(`/chat/${task.sessionId}`);
            } catch (error) {
                console.error('[TasksList] Failed to switch session:', error);
            }
        }
    };

    // Show loading state
    if (isLoading) {
        return <LoadingState message="Loading tasks..." />;
    }

    if (allTasks.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <CheckSquare className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
                <p className="text-sm text-muted-foreground mb-2">
                    {searchQuery ? 'No tasks match your search' : 'No async tasks yet'}
                </p>
                <p className="text-xs text-muted-foreground/70">
                    Tasks will appear here when you delegate to other agents or receive incoming delegations
                </p>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto p-4">
            <div className="space-y-2 max-w-2xl mx-auto">
                {allTasks.map((task) => (
                    <TaskCard
                        key={task.taskId}
                        task={task}
                        onAccept={handleAccept}
                        onReject={handleReject}
                        onDelete={handleDelete}
                        onViewDetails={handleViewDetails}
                    />
                ))}
            </div>
        </div>
    );
}
